import OpenInNewOutlined from '@mui/icons-material/OpenInNewRounded';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { Link } from '@buoyhealth/common.buoy-button';
import TreatmentSupplierIconComponent from '../Completion/components/TreatmentSupplierIcon';
import Prime from './prime.png';

import useImpressionTracking from 'src/hooks/useImpressionTracking';
import useInterviewType from 'src/interviews/hooks/useInterviewType';

import { trackProductClick, trackProductViewed } from './analytics';
import type { TreatmentSupplierIcon } from 'src/packages/polaris/types';

const CondensedCard = ({
  order,
  moduleLocation,
  url = '',
  imageUrl,
  header,
  source,
  isPrime,
  bulletPoints,
  className,
  scarcityPill,
}: {
  order: number;
  moduleLocation: string;
  url?: string;
  imageUrl?: string;
  header: string;
  source: string;
  isPrime?: boolean;
  bulletPoints: Array<{ icon: TreatmentSupplierIcon; label: React.ReactNode }>;
  className?: string;
  scarcityPill?: string;
}) => {
  const impressionId = useMemo(() => uuid(), []);
  const interviewType = useInterviewType();

  const impressionRef = useImpressionTracking(() => {
    trackProductViewed({
      impressionId,
      url,
      interviewType,
      order,
      moduleLocation,
    });
  });

  return (
    <Card
      className={`bp-w-[302px] bp-flex-shrink-0 bp-shadow-sm ${className}`}
      variant="outlined"
      ref={impressionRef}
    >
      <CardActionArea
        href={url}
        target="_blank"
        onClick={() => {
          trackProductClick({
            impressionId,
            url,
            interviewType,
            order,
            moduleLocation,
          });
        }}
        className="bp-group"
      >
        <CardContent>
          <div className="bp-flex bp-gap-2">
            {imageUrl && (
              <div className="bp-mr-2 bp-flex bp-h-[72px] bp-w-[72px] bp-flex-shrink-0 bp-items-center bp-justify-center">
                <img alt={header} src={imageUrl} className="bp-max-h-[72px] bp-max-w-[72px]" />
              </div>
            )}
            <CardHeader
              title={
                <Typography
                  variant="h5"
                  className="bp-line-clamp-2 bp-text-ellipsis bp-text-[18px]"
                >
                  {header}
                </Typography>
              }
              subheader={
                <div className="bp-mt-2 bp-flex bp-items-center">
                  <Link className="bp-mr-2 !bp-text-xs bp-no-underline group-hover:!bp-underline">
                    View on <span className="bp-capitalize">{source}</span>
                  </Link>
                  <OpenInNewOutlined color="primary" className="!bp-text-sm" />
                </div>
              }
              disableTypography
              className="bp-p-0"
            />
          </div>
          <div className="bp-mt-3">
            <div className="bp-font-primary-smaller bp-grid bp-gap-2 bp-text-gray-70">
              {bulletPoints.map((bp) => (
                <div className="bp-flex bp-items-start" key={bp.icon}>
                  <TreatmentSupplierIconComponent
                    type={bp.icon}
                    containerClassName="!bp-bg-gray-0"
                  />
                  <span className="bp-ml-2 bp-mt-1">{bp.label}</span>
                </div>
              ))}
              {scarcityPill && (
                <div className="bp-font-primary-smallest bp-flex bp-gap-2 bp-rounded-[8px] bp-bg-orange-0 bp-px-4 bp-py-1.5 bp-text-gray-100">
                  <div className="bp-font-primary-smaller">📣</div>
                  <div>{scarcityPill}</div>
                </div>
              )}
              {isPrime && (
                <img
                  src={Prime}
                  alt="Prime Logo"
                  className="bp-absolute bp-bottom-3 bp-right-3 bp-h-5"
                />
              )}
            </div>
          </div>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default CondensedCard;
